import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Play } from "lucide-react";
import axios from "axios";

const SAFFRON = "#C99A2E";
const SAFFRON_LIGHT = "#F2E8DD";
const IVORY = "#FEFCF7";
const INK = "#1A1A18";
const INK_MID = "#3D3D38";
const INK_SOFT = "#6B6B63";

function FilmStrip() {
  const [films, setFilms] = useState([]);
  useEffect(() => {
    axios.get(`/api/icons`).then(res => {
      const withEp = (res.data || []).filter(ic => (ic.episodes || []).length > 0);
      setFilms(withEp.slice(0, 3));
    }).catch(() => {});
  }, []);
  if (films.length === 0) return null;
  return (
    <div className="mt-14">
      <p className="uppercase text-xs tracking-widest font-semibold mb-6 text-center" style={{ color: SAFFRON }}>From the screening room</p>
      <div data-testid="film-strip" className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        {films.map(ic => {
          const ep = ic.episodes[0];
          let vid = "";
          try { vid = new URL(ep.youtube_url).searchParams.get("v") || ""; } catch { vid = ""; }
          return (
            <Link
              key={ic.slug}
              to="/conversations"
              data-testid={`film-card-${ic.slug}`}
              className="group block overflow-hidden transition-all duration-500 hover:-translate-y-1"
              style={{ borderRadius: '14px', background: '#fff', boxShadow: '0 10px 34px rgba(0,0,0,0.12)', border: '1px solid rgba(26,26,24,0.08)' }}
            >
              <div className="relative overflow-hidden" style={{ aspectRatio: '16/9', background: '#000' }}>
                {vid && (
                  <img
                    src={`https://img.youtube.com/vi/${vid}/hqdefault.jpg`}
                    alt={ep.title}
                    className="iab-bw-portrait w-full h-full object-cover"
                    loading="lazy"
                  />
                )}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center transition-transform duration-500 group-hover:scale-110"
                    style={{ border: '2px solid #C9A24B', background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(2px)' }}
                  >
                    <Play size={18} style={{ color: '#C9A24B', marginLeft: '2px' }} />
                  </div>
                </div>
              </div>
              <div className="p-4">
                <p className="text-sm font-bold leading-snug" style={{ color: INK }}>
                  {ic.name} — {ic.title_line}
                </p>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export default function BecausePage() {
  return (
    <div data-testid="because-page" className="min-h-screen" style={{ background: IVORY }}>
      {/* Hero */}
      <div className="flex items-center" style={{ background: `linear-gradient(160deg, ${INK} 0%, #2A2520 100%)`, minHeight: '68vh', paddingTop: '60px', paddingBottom: '60px' }}>
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center w-full">
          <p className="uppercase text-xs tracking-widest mb-8 font-semibold" style={{ color: SAFFRON }}>
            The Icons Collection
          </p>
          <h1 className="mb-8" style={{ fontSize: 'clamp(3rem, 9vw, 6.5rem)', fontFamily: "var(--iab-font-accent)", fontWeight: 500, color: '#FEFCF7', letterSpacing: '-0.02em', lineHeight: 1.05 }}>
            I am because{' '}
            <em style={{ color: '#C9A24B' }}>they were</em>
          </h1>
          <p className="max-w-lg mx-auto" style={{ fontFamily: "var(--iab-font-accent)", fontSize: '19px', lineHeight: 1.9, color: 'rgba(254,252,247,0.6)', fontStyle: 'italic' }}>
            Every person on iambecause has a why. The Icons of India are the nation's why — the architects whose vision built the India we live in.
          </p>
        </div>
      </div>

      {/* Bridge content */}
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-16">
        <div className="space-y-10">
          <div>
            <div className="mb-2" style={{ height: '2px', width: '40px', background: SAFFRON, borderRadius: '2px' }} />
            <p className="uppercase text-xs tracking-widest font-semibold mb-6" style={{ color: SAFFRON }}>The Connection</p>
            <p style={{ fontFamily: "var(--iab-font-accent)", fontSize: '17px', lineHeight: 1.9, color: INK_MID }}>
              On <span style={{ color: SAFFRON, fontWeight: 500 }}>iambecause</span>, ordinary people answer an extraordinary question: <em>Why do you do what you do?</em>
            </p>
            <p className="mt-5" style={{ fontFamily: "var(--iab-font-accent)", fontSize: '17px', lineHeight: 1.9, color: INK_MID }}>
              But there are some whose "why" shaped an entire nation. People whose conviction, vision, and decades of quiet service built the institutions, infrastructure and ideas that India stands on today.
            </p>
            <p className="mt-5" style={{ fontFamily: "var(--iab-font-accent)", fontSize: '17px', lineHeight: 1.9, color: INK_MID }}>
              <strong style={{ color: INK }}>Icons of India</strong> is a legacy room dedicated to such nation-builders — anchored by the visionary Sam Pitroda, whose telecom revolution connected a billion people. Their stories are not taught in textbooks, but they should be.
            </p>
          </div>

          {/* Two paths */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12">
            <Link
              to="/icons"
              data-testid="go-to-icons"
              className="group block p-8 rounded-2xl transition-all hover:shadow-lg"
              style={{ background: `linear-gradient(135deg, ${SAFFRON_LIGHT}, #FFF8F0)`, border: `1px solid rgba(201,154,46,0.15)` }}
            >
              <p className="uppercase text-xs tracking-widest font-semibold mb-3" style={{ color: SAFFRON }}>Explore</p>
              <h3 className="text-xl font-medium mb-3" style={{ color: INK }}>Meet the 21 Icons</h3>
              <p className="text-sm mb-5" style={{ color: INK_SOFT, lineHeight: 1.7 }}>
                Discover the nation-builders — their biographies, conversations, and enduring legacies.
              </p>
              <span className="inline-flex items-center gap-2 text-sm font-semibold transition-all group-hover:gap-3" style={{ color: SAFFRON }}>
                Browse Icons <ArrowRight size={16} />
              </span>
            </Link>

            <Link
              to="/"
              data-testid="go-to-stories"
              className="group block p-8 rounded-2xl transition-all hover:shadow-lg"
              style={{ background: '#fff', border: '1px solid rgba(26,26,24,0.08)' }}
            >
              <p className="uppercase text-xs tracking-widest font-semibold mb-3" style={{ color: 'var(--iab-amber)' }}>Stories</p>
              <h3 className="text-xl font-medium mb-3" style={{ color: INK }}>Read People's Stories</h3>
              <p className="text-sm mb-5" style={{ color: INK_SOFT, lineHeight: 1.7 }}>
                Hear from everyday people answering the one question nobody asks — why do you do what you do?
              </p>
              <span className="inline-flex items-center gap-2 text-sm font-semibold transition-all group-hover:gap-3" style={{ color: 'var(--iab-amber)' }}>
                Read Stories <ArrowRight size={16} />
              </span>
            </Link>
          </div>

          <FilmStrip />
        </div>
      </div>
    </div>
  );
}
